import { useState } from 'react';
import { Button, Input, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, GiftOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { StaffAsyncSelect } from './StaffAsyncSelect';
import { SerialSwapDialog } from './SerialSwapDialog';
import type { OrderItem, FulfillmentMode } from '../types';
import type { PosItemError } from '../utils/validatePosItems';
import './OrderItemsTable.css';

const { Text } = Typography;

function formatPrice(n: number) {
  return n.toLocaleString('vi-VN');
}

/** Strip non-digit + format thousand separators trong lúc gõ. */
function formatPriceInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('vi-VN');
}

/** Parse "1.000.000" → 1000000. Bỏ mọi ký tự không phải digit. */
function parsePrice(s: string): number {
  const digits = s.replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : 0;
}

/** Parse decimal cho CK% — giữ dấu chấm. */
function parseDecimal(s: string): number {
  return parseFloat(s.replace(/[^\d.]/g, '')) || 0;
}

interface Props {
  items: OrderItem[];
  fulfillmentMode: FulfillmentMode;
  storeId?: string;
  onUpdateItem: (id: string, patch: Partial<OrderItem>) => void;
  onRemoveItem: (id: string) => void;
  onSwapSerial: (itemId: string, serial: string) => Promise<{ ok: boolean; error?: string }>;
  swapping?: boolean;
  staleItemIds?: Set<string>;
  /** Preview prices for stale items (item.id → new price computed by BE).
   *  Used to render "old → new" inline so cashier sees the impending change
   *  before clicking "Cập nhật giá" in the banner. */
  stalePreviewByItemId?: Map<string, { price: number }>;
  /** Per-item required-field errors keyed by item id (seller / warehouse). */
  itemErrors?: Record<string, PosItemError>;
}

function TableHeader({ showWarehouseCol }: { showWarehouseCol: boolean }) {
  const th: React.CSSProperties = {
    padding: '8px 6px',
    fontSize: 13,
    fontWeight: 700,
    color: '#333',
    background: '#fafafa',
    borderBottom: '1px solid #e8e8e8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 0,
  };

  return (
    <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', background: '#fafafa', flexShrink: 0 }}>
      <div style={{ ...th, width: 36, textAlign: 'center' }}>#</div>
      <div style={{ ...th, flex: 1, minWidth: 160 }}>Tên Hàng</div>
      <div style={{ ...th, width: 88, textAlign: 'center' }}>Mã QC</div>
      <div style={{ ...th, width: 130, textAlign: 'right' }}>Giá Tiền</div>
      <div style={{ ...th, width: 64, textAlign: 'center' }}>CK%</div>
      <div style={{ ...th, width: 120, textAlign: 'center' }}>CK</div>
      <div style={{ ...th, width: 52, textAlign: 'center' }}>SL</div>
      <div style={{ ...th, width: 52, textAlign: 'center' }}>TL</div>
      <div style={{ ...th, width: 72, textAlign: 'right' }}>T.Công</div>
      <div style={{ ...th, width: 96, textAlign: 'right' }}>T.Tiền</div>
      <div style={{ ...th, width: 128, textAlign: 'center' }}>N.Bán</div>
      {showWarehouseCol && <div style={{ ...th, width: 128, textAlign: 'center' }}>C.Kho</div>}
      <div style={{ ...th, width: 36, textAlign: 'center' }}>TT</div>
    </div>
  );
}

function OrderItemRow({
  item,
  index,
  fulfillmentMode,
  showWarehouseCol,
  storeId,
  isStale,
  previewPrice,
  error,
  onUpdate,
  onRemove,
  onEditSerial,
}: {
  item: OrderItem;
  index: number;
  fulfillmentMode: FulfillmentMode;
  showWarehouseCol: boolean;
  storeId?: string;
  isStale: boolean;
  previewPrice?: number;
  error?: PosItemError;
  onUpdate: (patch: Partial<OrderItem>) => void;
  onRemove: () => void;
  onEditSerial: () => void;
}) {
  const isQuantityMode =
    item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  const isSlEditable = isQuantityMode;

  // Editing state per field — null khi không focus (hiển thị giá trị committed
  // đã format); string khi user đang gõ (hiển thị raw input value). Pattern này
  // copy từ ancarat-fe để hỗ trợ real-time validation + thousand separator.
  const [editPrice, setEditPrice] = useState<string | null>(null);
  const [editCkPct, setEditCkPct] = useState<string | null>(null);
  const [editCkAmt, setEditCkAmt] = useState<string | null>(null);
  const [editQty, setEditQty] = useState<string | null>(null);

  const td: React.CSSProperties = {
    padding: '8px 6px',
    fontSize: 13,
    borderBottom: '1px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  // ---- Price ----
  const priceDisplay = editPrice !== null ? editPrice : formatPrice(item.price);

  function handlePriceFocus() {
    setEditPrice(formatPrice(item.price));
  }
  function handlePriceChange(raw: string) {
    setEditPrice(formatPriceInput(raw));
  }
  function handlePriceBlur() {
    const parsed = parsePrice(editPrice ?? '');
    setEditPrice(null);
    if (parsed === item.price || parsed <= 0) return;
    const discPerUnit = (parsed * item.discountPercent) / 100;
    const discountAmount = isQuantityMode
      ? Math.round(discPerUnit * item.quantity)
      : Math.round(discPerUnit);
    const totalAmount = isQuantityMode
      ? parsed * item.quantity - discountAmount
      : parsed - discountAmount;
    onUpdate({ price: parsed, discountAmount, totalAmount });
  }

  // ---- CK% ----
  const ckPctDisplay = editCkPct !== null ? editCkPct : String(item.discountPercent);

  function handleCkPctFocus() {
    setEditCkPct(String(item.discountPercent));
  }
  function handleCkPctChange(raw: string) {
    // Real-time guard: cho phép rỗng hoặc digit + 1 dấu chấm. Reject nếu vượt 100.
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    const num = parseFloat(raw);
    if (raw !== '' && !Number.isNaN(num) && num > 100) return;
    setEditCkPct(raw);
  }
  function handleCkPctBlur() {
    let parsed = parseDecimal(editCkPct ?? '');
    parsed = Math.min(100, Math.max(0, parsed));
    setEditCkPct(null);
    if (parsed === item.discountPercent) return;
    const discPerUnit = (item.price * parsed) / 100;
    const discountAmount = isQuantityMode
      ? Math.round(discPerUnit * item.quantity)
      : Math.round(discPerUnit);
    const totalAmount = isQuantityMode
      ? item.price * item.quantity - discountAmount
      : item.price - discountAmount;
    onUpdate({ discountPercent: parsed, discountAmount, totalAmount });
  }

  // ---- CK amount ----
  const ckAmtDisplay =
    editCkAmt !== null ? editCkAmt : formatPrice(item.discountAmount);

  function handleCkAmtFocus() {
    // Mirror Price column: hiển thị giá trị đã format ngay khi focus + giữ
    // thousand separator trong lúc gõ + sau khi blur (display fall back về
    // formatPrice(item.discountAmount)). Trước đây focus set raw "2000000"
    // nên user thấy mất dấu chấm phân cách — EG-1757.
    setEditCkAmt(formatPrice(item.discountAmount));
  }
  function handleCkAmtChange(raw: string) {
    setEditCkAmt(formatPriceInput(raw));
  }
  function handleCkAmtBlur() {
    const parsedAmount = parsePrice(editCkAmt ?? '');
    setEditCkAmt(null);
    const maxDiscount = isQuantityMode ? item.price * item.quantity : item.price;
    const clampedAmount = Math.min(Math.max(parsedAmount, 0), maxDiscount);
    if (clampedAmount === item.discountAmount) return;
    const perUnitAmount = isQuantityMode
      ? item.quantity > 0
        ? clampedAmount / item.quantity
        : 0
      : clampedAmount;
    const discountPercent =
      item.price > 0 ? Math.round((perUnitAmount / item.price) * 100 * 100) / 100 : 0;
    const totalAmount = isQuantityMode
      ? item.price * item.quantity - clampedAmount
      : item.price - clampedAmount;
    onUpdate({ discountPercent, discountAmount: clampedAmount, totalAmount });
  }

  // ---- Quantity ----
  const qtyDisplay = editQty !== null ? editQty : String(item.quantity);

  function handleQtyFocus() {
    setEditQty(String(item.quantity));
  }
  function handleQtyChange(raw: string) {
    if (raw !== '' && !/^\d*$/.test(raw)) return;
    setEditQty(raw);
  }
  function handleQtyBlur() {
    const parsed = parseInt(editQty ?? '', 10);
    const valid = Number.isNaN(parsed) ? 1 : Math.max(1, parsed);
    setEditQty(null);
    if (valid === item.quantity) return;
    const discPerUnit = (item.price * item.discountPercent) / 100;
    const discountAmount = Math.round(discPerUnit * valid);
    const totalAmount = item.price * valid - discountAmount;
    onUpdate({ quantity: valid, discountAmount, totalAmount });
  }

  return (
    <div id={`pos-item-row-${item.id}`} className="pos-order-row" style={{ display: 'flex', borderBottom: '1px solid #f5f5f5' }}>
      {/* STT */}
      <div style={{ ...td, width: 36, justifyContent: 'center', color: '#999' }}>{index + 1}</div>

      {/* Tên Hàng */}
      <div style={{ ...td, flex: 1, minWidth: 160, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {item.variantName}
          </Text>
          {item.flash_sale_id && (
            <Tag icon={<ThunderboltOutlined />} color="red" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
              Flash
            </Tag>
          )}
          {item.is_gift && (
            <Tag icon={<GiftOutlined />} color="green" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
              Quà tặng
            </Tag>
          )}
        </div>
        <Text style={{ fontSize: 11, color: '#888', whiteSpace: 'normal', wordBreak: 'break-word' }}>
          Mã biến thể: {item.variantCode}
        </Text>
      </div>

      {/* Mã QC (serial) — bấm để đổi nếu dòng có serial */}
      <div style={{ ...td, width: 88, justifyContent: 'center', fontSize: 12 }}>
        {item.specificationCode ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontSize: 12 }}
            onClick={onEditSerial}
          >
            {item.specificationCode}
          </Button>
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        )}
      </div>

      {/* Giá Tiền — luôn render Input (giống CK%/CK/SL). Preview giá mới hiển thị
          dưới input khi stale. */}
      <div
        style={{
          ...td,
          width: 130,
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: 2,
          lineHeight: 1.2,
        }}
      >
        {(() => {
          const showPreview =
            isStale && previewPrice !== undefined && previewPrice !== item.price;
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {isStale && (
                  <Tooltip
                    title={
                      previewPrice !== undefined
                        ? `Giá cũ ${formatPrice(item.price)} — giá mới ${formatPrice(previewPrice)}`
                        : 'Giá cũ — đã có giá mới từ thị trường'
                    }
                  >
                    <span style={{ color: '#ff4d4f', fontSize: 10, lineHeight: 1 }}>●</span>
                  </Tooltip>
                )}
                {item.appliedOverride && (
                  <Tooltip title="Đã áp giá riêng của cửa hàng">
                    <span style={{ color: '#a4193d', fontSize: 10, lineHeight: 1 }}>⁕</span>
                  </Tooltip>
                )}
                <Input
                  style={{ flex: 1, minWidth: 0, textAlign: 'right' }}
                  value={priceDisplay}
                  onFocus={handlePriceFocus}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  onBlur={handlePriceBlur}
                  onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                  inputMode="numeric"
                />
              </div>
              {showPreview && (
                <Tooltip title="Sẽ áp giá này khi bấm “Cập nhật giá” trên banner">
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: previewPrice > item.price ? '#cf1322' : '#389e0d',
                      textAlign: 'right',
                    }}
                  >
                    {formatPrice(previewPrice)}
                  </span>
                </Tooltip>
              )}
            </>
          );
        })()}
      </div>

      {/* CK% */}
      <div style={{ ...td, width: 64, justifyContent: 'center' }}>
        <Input
          style={{ width: '100%', textAlign: 'center' }}
          value={ckPctDisplay}
          onFocus={handleCkPctFocus}
          onChange={(e) => handleCkPctChange(e.target.value)}
          onBlur={handleCkPctBlur}
          onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
          inputMode="decimal"
        />
      </div>

      {/* CK amount */}
      <div style={{ ...td, width: 120, justifyContent: 'flex-end' }}>
        <Input
          style={{ width: '100%', textAlign: 'right' }}
          value={ckAmtDisplay}
          onFocus={handleCkAmtFocus}
          onChange={(e) => handleCkAmtChange(e.target.value)}
          onBlur={handleCkAmtBlur}
          onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
          inputMode="numeric"
        />
      </div>

      {/* SL */}
      <div style={{ ...td, width: 52, justifyContent: 'center' }}>
        {isSlEditable ? (
          <Input
            style={{ width: '100%', textAlign: 'center' }}
            value={qtyDisplay}
            onFocus={handleQtyFocus}
            onChange={(e) => handleQtyChange(e.target.value)}
            onBlur={handleQtyBlur}
            onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
            inputMode="numeric"
          />
        ) : (
          <Text style={{ fontSize: 13, color: '#999' }}>1</Text>
        )}
      </div>

      {/* TL (weight) */}
      <div style={{ ...td, width: 52, justifyContent: 'center', color: '#555', fontSize: 13 }}>
        {item.weight != null ? item.weight.toFixed(2) : '—'}
      </div>

      {/* T.Công */}
      <div style={{ ...td, width: 72, justifyContent: 'flex-end', fontSize: 13 }}>
        {item.laborFee != null ? formatPrice(item.laborFee) : '—'}
      </div>

      {/* T.Tiền — compact 2-line preview to match price column */}
      <div
        style={{
          ...td,
          width: 96,
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 0,
          lineHeight: 1.2,
        }}
      >
        {(() => {
          const showPreview =
            isStale && previewPrice !== undefined && previewPrice !== item.price;
          if (!showPreview) {
            return (
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {formatPrice(item.totalAmount)}
              </span>
            );
          }
          const isQty = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
          const discPerUnit = (previewPrice * item.discountPercent) / 100;
          const newDiscount = isQty
            ? Math.round(discPerUnit * item.quantity)
            : Math.round(discPerUnit);
          const newTotal = isQty
            ? previewPrice * item.quantity - newDiscount
            : previewPrice - newDiscount;
          return (
            <>
              <span style={{ fontSize: 11, textDecoration: 'line-through', color: '#999' }}>
                {formatPrice(item.totalAmount)}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: newTotal > item.totalAmount ? '#cf1322' : '#389e0d',
                }}
              >
                {formatPrice(newTotal)}
              </span>
            </>
          );
        })()}
      </div>

      {/* N.Bán */}
      <div style={{ ...td, width: 128 }}>
        <StaffAsyncSelect
          value={item.sellerStaffId}
          onChange={(staffId) => onUpdate({ sellerStaffId: staffId })}
          storeId={storeId}
          placeholder="NV"
          error={error?.seller}
          errorText="Bắt buộc chọn nhân viên bán"
        />
      </div>

      {/* C.Kho */}
      {showWarehouseCol && (
        <div style={{ ...td, width: 128 }}>
          <StaffAsyncSelect
            value={item.warehouseStaffId}
            onChange={(staffId) => onUpdate({ warehouseStaffId: staffId })}
            storeId={storeId}
            allowedStaffIds={item.warehouseStaffIds}
            placeholder="Kho"
            warning={item.warehouseStaffIds !== undefined && item.warehouseStaffIds.length === 0}
            error={error?.warehouse}
            errorText="Bắt buộc chọn nhân viên kho"
          />
        </div>
      )}

      {/* TT - Delete */}
      <div style={{ ...td, width: 36, justifyContent: 'center' }}>
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined style={{ fontSize: 14 }} />}
          onClick={onRemove}
        />
      </div>
    </div>
  );
}

export function OrderItemsTable({
  items, fulfillmentMode, storeId, onUpdateItem, onRemoveItem,
  onSwapSerial, swapping, staleItemIds, stalePreviewByItemId, itemErrors,
}: Props) {
  const showWarehouseCol = fulfillmentMode !== 'PREORDER';
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  // Derive live item từ items mỗi render → dialog luôn phản ánh data hiện tại và
  // tự đóng nếu dòng bị xoá khi dialog đang mở.
  const editingItem = items.find((i) => i.id === editingItemId) ?? null;

  const handleConfirmSwap = async (serial: string) => {
    if (!editingItem) return;
    const res = await onSwapSerial(editingItem.id, serial);
    if (res.ok) {
      void message.success('Đã đổi mã quy cách');
      setEditingItemId(null);
    } else {
      void message.error(res.error ?? 'Không thể đổi mã quy cách');
    }
  };

  if (items.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
        Quét QR hoặc nhập mã để thêm sản phẩm
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <TableHeader showWarehouseCol={showWarehouseCol} />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {items.map((item, index) => (
          <OrderItemRow
            key={item.id}
            item={item}
            index={index}
            fulfillmentMode={fulfillmentMode}
            showWarehouseCol={showWarehouseCol}
            storeId={storeId}
            isStale={staleItemIds?.has(item.id) ?? false}
            previewPrice={stalePreviewByItemId?.get(item.id)?.price}
            error={itemErrors?.[item.id]}
            onUpdate={(patch) => onUpdateItem(item.id, patch)}
            onRemove={() => onRemoveItem(item.id)}
            onEditSerial={() => setEditingItemId(item.id)}
          />
        ))}
      </div>
      {editingItem && (
        <SerialSwapDialog
          open={!!editingItem}
          onClose={() => setEditingItemId(null)}
          variantId={editingItem.variantId}
          productName={editingItem.variantName}
          currentSerial={editingItem.specificationCode}
          usedSerials={items
            .filter((i) => i.id !== editingItem.id && i.specificationCode)
            .map((i) => i.specificationCode as string)}
          storeId={storeId}
          submitting={swapping}
          onConfirm={handleConfirmSwap}
        />
      )}
    </div>
  );
}
